// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Wire habitrails (M1-21, PH-A17).
//
// A habitrail is not an obstacle, it is a **constraint**. A ball in a wire cradle has one
// degree of freedom: distance along the wire. Modelling it as two thin rails the ball
// bounces between would be both more expensive and less stable — thin rails are exactly the
// geometry a discrete contact solver handles worst, and a ball rattling down a wire looks
// wrong even when it arrives in the right place.
//
// So the wire takes ownership of the ball's motion, exactly as a scoop does, and moves it
// along its own arc length. Gravity along the wire is the honest projection of the playfield
// gravity vector onto the three-dimensional tangent:
//
//     a = g · t̂  =  −a_downfield · t̂y  −  a_normal · t̂z
//
// which means a habitrail that climbs slows the ball, one that falls away speeds it up, and
// one that runs level across the playfield still drifts it gently downfield. All three are
// what a real wireform does, and none of them is special-cased.

import {CapsuleChain,type CapsuleProfile} from '../Capsule'
import type { Ball } from '../Ball'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { Path } from '../Path'
import { EV_SWITCH_CLOSED, EV_SWITCH_OPENED } from '../events'
import { A_DOWNFIELD, A_NORMAL, BALL_RADIUS, MAX_BALLS } from '../constants'

export interface WireGuideConfig {
  readonly id: string
  readonly supports?:readonly CapsuleProfile[]
  /** Centreline control points, playfield-local, with height. */
  readonly spline: readonly (readonly [number, number, number])[]
  /** How near the entry a ball has to be to be caught, metres. */
  readonly captureRadius?: number
  /** Layer a ball is caught from. Default 0, the playfield. */
  readonly fromLayer?: number
  /** Layer a ball is released onto at the far end. Defaults to `fromLayer`. */
  readonly toLayer?: number
  /** Slowest ball the entry will accept, m/s. */
  readonly minEntrySpeed?: number
  /** Rolling drag along the wire, per second. */
  readonly drag?: number
  readonly samplesPerSegment?: number
}

export class WireGuide extends Device {
  readonly kind = 'wireGuide'
  override readonly wantsContact=true
  private readonly supports:CapsuleChain
  override resolveContact(world:World,ball:Ball):boolean{return this.supports.resolve(world,ball)}
  override readonly wantsObserve = true

  readonly path: Path
  readonly captureRadius: number
  readonly fromLayer: number
  readonly toLayer: number

  private readonly minEntrySpeed: number
  private readonly drag: number
  private readonly point = { x: 0, y: 0, z: 0 }
  private readonly tangent = { x: 0, y: 0, z: 0 }

  /** Arc length and along-wire speed for each ball this wire owns. */
  private readonly arc = new Float64Array(MAX_BALLS)
  private readonly speed = new Float64Array(MAX_BALLS)
  private readonly riding = new Uint8Array(MAX_BALLS)

  /**
   * The three arrays above, which is the whole of a wire's mutable state (M4-28a).
   *
   * **This was missing, and it stranded balls.** `Ball.heldBy` is snapshotted and says "device
   * 28 owns this ball"; `riding` is what device 28 uses to decide it has anything to move. A
   * restore that brought back the first and not the second produced a ball that was held by a
   * habitrail the habitrail had never heard of — so `step` skipped it, nothing released it, and
   * it sat at the rail entry for the rest of the game while the uninterrupted run carried it
   * round to the scoop.
   *
   * The consequence is not cosmetic. Validation of a long replay runs in chunks, so a game with
   * a ball on a habitrail at a chunk boundary re-simulates differently from the one that was
   * recorded — and the player is told `mismatch`, which reads as an accusation. Found by probing
   * for it after the C# port asked why this device alone had no `ownState`.
   *
   * `arc` and `speed` matter for the same reason one step later: a ball restored as riding but
   * at arc zero re-enters the wire at its entry, and a ball restored with no speed stops on the
   * first climb.
   */
  protected override ownState(): DeviceState {
    return { arc: [...this.arc], speed: [...this.speed], riding: [...this.riding] }
  }

  protected override restoreOwn(state: DeviceState): void {
    const arc = state['arc']
    const speed = state['speed']
    const riding = state['riding']
    if (Array.isArray(arc)) this.arc.set(arc as number[])
    if (Array.isArray(speed)) this.speed.set(speed as number[])
    if (Array.isArray(riding)) this.riding.set(riding as number[])
  }

  constructor(config: WireGuideConfig) {
    super(config.id)
    this.supports=new CapsuleChain(config.supports??[])
    this.path = new Path(config.spline, config.samplesPerSegment ?? 12)
    this.setOrigin(config.spline[0]![0], config.spline[0]![1])
    this.captureRadius = config.captureRadius ?? 0.016
    this.fromLayer = config.fromLayer ?? 0
    this.toLayer = config.toLayer ?? this.fromLayer
    this.minEntrySpeed = config.minEntrySpeed ?? 0.8
    this.drag = config.drag ?? 0.35
  }

  get entryX(): number {
    return this.path.xs[0]!
  }
  get entryY(): number {
    return this.path.ys[0]!
  }
  get entryZ(): number {
    return this.path.zs[0]!
  }

  /** Is this ball currently on the wire? */
  carries(ball: Ball): boolean {
    return this.riding[ball.id] !== 0
  }

  override step(world: World, dt: number): void {
    for (let id = 0; id < MAX_BALLS; id++) {
      if (this.riding[id] === 0) continue
      const ball = world.balls[id]
      if (ball === undefined || !ball.active) {
        this.riding[id] = 0
        continue
      }
      this.advance(world, ball, dt)
    }
  }

  private advance(world: World, ball: Ball, dt: number): void {
    const id = ball.id
    let s = this.arc[id]!
    let v = this.speed[id]!

    this.path.tangentAt(s, this.tangent)
    // Gravity resolved onto the wire. No normal force, no friction against a surface — a
    // ball in a wire cradle touches only the rails.
    v += (-A_DOWNFIELD * this.tangent.y - A_NORMAL * this.tangent.z) * dt
    v -= v * this.drag * dt
    s += v * dt

    if (s >= this.path.length) {
      this.release(world, ball, this.path.length, v, this.toLayer)
      return
    }
    if (s <= 0) {
      this.release(world, ball, 0, v, this.fromLayer)
      return
    }

    this.arc[id] = s
    this.speed[id] = v

    this.path.pointAt(s, this.point)
    this.path.tangentAt(s, this.tangent)
    ball.x = this.point.x
    ball.y = this.point.y
    ball.z = this.point.z + BALL_RADIUS
    ball.floorZ = this.point.z
    ball.vx = this.tangent.x * v
    ball.vy = this.tangent.y * v
    ball.vz = this.tangent.z * v

    // Roll the ball at the rate its travel implies, so PH-A16 holds on a wire as well as on
    // the playfield: ω = (ẑ × v̂) · v / r.
    const rate = v / BALL_RADIUS
    ball.wx = -this.tangent.y * rate
    ball.wy = this.tangent.x * rate
    ball.wz = 0
  }

  override observe(world: World, ball: Ball): void {
    if (ball.layer !== this.fromLayer) return
    if (ball.heldBy >= 0) return

    const dx = ball.x - this.entryX
    const dy = ball.y - this.entryY
    const dz = ball.z - BALL_RADIUS - this.entryZ
    if (dx * dx + dy * dy + dz * dz > this.captureRadius * this.captureRadius) {
      this.spill(world, ball)
      return
    }

    this.path.tangentAt(0, this.tangent)
    const along =
      ball.vx * this.tangent.x + ball.vy * this.tangent.y + ball.vz * this.tangent.z
    if (along < this.minEntrySpeed) {
      this.spill(world, ball)
      return
    }

    this.arc[ball.id] = 0
    this.speed[ball.id] = along
    this.riding[ball.id] = 1
    ball.heldBy = this.index

    // Snap onto the rail at once. The ball reached here under free flight, so it is a
    // fraction of a millimetre off the centreline; leaving that until the next step would
    // put it briefly on a wire it is not quite touching.
    this.path.pointAt(0, this.point)
    ball.x = this.point.x
    ball.y = this.point.y
    ball.z = this.point.z + BALL_RADIUS
    ball.floorZ = this.point.z
    ball.vx = this.tangent.x * along
    ball.vy = this.tangent.y * along
    ball.vz = this.tangent.z * along

    world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, along)
  }

  /**
   * A ball this habitrail did not catch, put down where it is.
   *
   * A wire's own layer carries **no geometry at all** — the rails are not colliders, they are
   * a path the ball is glued to. So a ball delivered onto this layer by a ramp and then not
   * captured has nothing whatever to stop it: it keeps whatever velocity it arrived with and
   * leaves the cabinet. The soak found exactly this, twice in two hundred balls, on a ramp
   * whose habitrail started by heading the *opposite* way to the ramp exit, so the entry
   * tangent test refused every ball.
   *
   * A ball that misses the habitrail falls onto the playfield, which is both the physical
   * answer and the safe one.
   */
  private spill(world: World, ball: Ball): void {
    // Nothing to put down. A ball that this wire is not carrying, already on the layer it would
    // be spilled to, is a ball going about its business somewhere else on the machine.
    //
    // Without this the method fires on **every step**, for every free ball, whenever a wire's
    // `fromLayer` is the playfield — and it emits a switch event each time. The loader never
    // produces that wiring for a real machine (a habitrail's `fromLayer` is its own layer), so
    // nothing shipped with a two-thousand-event-a-second fire-hose in it; the parity rig, whose
    // wires take the documented default of 0, walked straight into it. The event is wrong in
    // either case: `switchOpened` names a transition, and a ball that is where it already was has
    // not transitioned.
    const wasRiding = this.riding[ball.id] !== 0
    if (!wasRiding && ball.layer === this.toLayer) return

    ball.layer = this.toLayer
    ball.heldBy = -1
    this.riding[ball.id] = 0
    ball.floorZ = 0
    if (ball.z < BALL_RADIUS) ball.z = BALL_RADIUS
    world.events.emit(EV_SWITCH_OPENED, this.index, ball.id, world.stepIndex, ball.speed())
  }

  private release(world: World, ball: Ball, s: number, v: number, layer: number): void {
    this.path.pointAt(s, this.point)
    this.path.tangentAt(s, this.tangent)

    ball.heldBy = -1
    ball.layer = layer
    this.riding[ball.id] = 0

    ball.x = this.point.x
    ball.y = this.point.y
    ball.floorZ = layer === 0 ? 0 : this.point.z
    ball.z = Math.max(this.point.z, ball.floorZ) + BALL_RADIUS
    ball.vx = this.tangent.x * v
    ball.vy = this.tangent.y * v
    ball.vz = this.tangent.z * v

    world.events.emit(EV_SWITCH_OPENED, this.index, ball.id, world.stepIndex, v)
  }

  /** Where the wire ends and how fast a ball arriving at `entrySpeed` gets there.
   *  Used by the Builder to check a habitrail is reachable before anyone plays it. */
  exitSpeedFor(entrySpeed: number, dt = 0.001): number {
    let s = 0
    let v = entrySpeed
    let guard = 0
    while (s < this.path.length && v > 0 && guard < 200000) {
      this.path.tangentAt(s, this.tangent)
      v += (-A_DOWNFIELD * this.tangent.y - A_NORMAL * this.tangent.z) * dt
      v -= v * this.drag * dt
      s += v * dt
      guard++
    }
    return v > 0 ? v : 0
  }

  override reset(): void {
    this.arc.fill(0)
    this.speed.fill(0)
    this.riding.fill(0)
  }
}
